<template>
    <div :class="elementClass">
        <date-picker
            :key="lang"
            :type="type"
            :lang="lang"
            v-model="date"
            :range="range"
            :format="format"
            @change="change"
            :title-format="format"
            :clearable="clearable"
            :value-type="valueType"
            :placeholder="placeholder"
            :disabled-date="disabledDate"
            popup-class="z-date-picker__popup"
        >
            <template v-slot:icon-calendar>
                <z-icon
                    width="21"
                    height="20"
                    name="calendar"
                />
            </template>
        </date-picker>
    </div>
</template>

<script>
import 'vue2-datepicker/index.css'
import DatePicker from 'vue2-datepicker'

export default {
    name: 'z-date-picker',
    components: {
        DatePicker
    },
    props: {
        range: Boolean,
        disabled: Boolean,
        clearable: Boolean,
        type: {
            type: String,
            default: 'date',
            validator: prop => ['date', 'datetime', 'year', 'month', 'time', 'week'].includes(prop)
        },
        valueType: {
            type: String,
            default: 'X'
        },
        placeholder: {
            type: String,
            default: ''
        },
        format: {
            type: String,
            default: 'DD.MM.YYYY'
        },
        lang: {
            type: String,
            default: 'en'
        },
        value: [Array, String],
        disabledDate: {
            type: Function,
            default: () => {}
        }
    },
    data () {
        return {
            date: this.value
        }
    },
    created () {
        import(`vue2-datepicker/locale/${this.lang}`)
    },
    watch: {
        value (value) {
            this.date = value
        }
    },
    computed: {
        elementClass () {
            return [
                'z-date-picker',
                {
                    'is-disabled': this.disabled
                }
            ]
        }
    },
    methods: {
        change (data) {
            let result = data

            if (Array.isArray(data) && !(data[0] && data[1])) {
                result = undefined
            }

            this.$emit('input', result)
            this.$emit('change', result)
        }
    }
}
</script>

<style lang="scss">
.z-date-picker {
    $padding: 16px;

    width: 100%;
    height: 64px;
    font-family: inherit;
    background-color: $token-colors-white;
    box-shadow: inset 0 0 0 1px $token-colors-black-20;
    user-select: none;
    transition: box-shadow $transition;
    cursor: pointer;

    &:hover {
        box-shadow: inset 0 0 0 1px #EE7918;
    }

    .mx-datepicker {
        max-width: none;
        width: 100%;
        height: 100%;
    }

    .mx-input-wrapper {
        display: flex;
        align-items: center;
        flex-flow: row nowrap;
        width: 100%;
        height: 100%;
        padding: 0 24px;
        position: relative;
        background-color: transparent;
    }

    .mx-icon-clear {
        position: absolute;
        display: inline-block;
        top: 50%;
        right: 55px;
        transform: translateY(-50%);
        width: 24px;
        height: 24px;
        vertical-align: middle;
        background-color: $token-colors-white;
        box-shadow: inset 0 0 0 1px $token-colors-black-20;
        border-radius: 50%;
        transition: background-color $transition, box-shadow $transition;
        z-index: 2;

        svg {
            display: none;
        }

        &:before,
        &:after {
            content: '';
            display: block;
            width: 13px;
            height: 2px;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #E42213;
            margin: auto;
            transition: background-color $transition;
            z-index: 1;
        }

        &:before {
            transform: rotate(45deg);
        }

        &:after {
            transform: rotate(-45deg);
        }

        &:hover {
            background-color: #EE7918;
            box-shadow: none;

            &:before,
            &:after {
                background-color: $token-colors-white;
            }
        }

        @include breakpoint(v-tablet) {
            right: 52px;
        }
    }

    .mx-icon-calendar {
        display: flex;
        cursor: pointer;
    }

    .mx-input {
        width: calc(100% - 20px);
        height: auto;
        border: none;
        padding: 0;
        font-size: 18px;
        font-weight: 400;
        line-height: 1.2;
        color: $token-colors-black;
        background-color: transparent;
        box-shadow: none;

        &:hover,
        &:focus {
            outline: 0 none;
        }

        &:invalid {
            box-shadow: none;
        }

        &::-ms-clear {
            display: none;
        }

        &::placeholder {
            white-space: nowrap;
            text-overflow: ellipsis;
            color: rgba($token-colors-black, .4);
        }

        @include breakpoint(v-tablet) {
            font-size: 16px;
        }
    }

    &__popup {
        z-index: 10;

        button {
            border: none;
            background-color: transparent;
            box-shadow: none;
        }

        .mx-datepicker-content {
            padding: 24px 32px;
            background-color: $token-colors-white;
            box-shadow: 0 17px 10px rgba($token-colors-black, 0.05);

            @include breakpoint (mobile) {
                padding: 16px 24px;
            }
        }

        .mx-range-wrapper {
            display: flex;
            flex-flow: row nowrap;
            align-items: flex-start;
            justify-content: flex-start;

            .mx-calendar+.mx-calendar {
                margin-left: 16px;
            }

            @include breakpoint(mobile) {
                flex-direction: column;

                .mx-calendar+.mx-calendar {
                    margin-left: 0;
                    margin-top: 16px;
                }
            }
        }

        .mx-calendar-header {
            display: flex;
            align-items: center;
            margin-bottom: 16px;

            &-label {
                display: flex;
                justify-content: center;
                flex: 0 1 100%;
                order: 2;
                font-size: 22px;
                font-weight: 500;
                line-height: 1.2;
                color: $token-colors-black;
                text-transform: capitalize;

                @include breakpoint (mobile) {
                    font-size: 16px;
                }

                > span {
                    padding: 1px 0;
                }

                .mx-calendar-decade-separator {
                    padding: 0 4px;

                    &::after {
                        content: '-';
                    }
                }
            }

            .mx-btn-current-month {
                text-transform: capitalize;
            }

            .mx-btn-current-year {
                color: #E42213;
            }
        }

        .mx-calendar-content {
            height: 244px;

            @include breakpoint (mobile) {
                height: auto;
            }
        }

        .mx-btn-icon-left,
        .mx-btn-icon-double-left,
        .mx-btn-icon-right,
        .mx-btn-icon-double-right {
            flex: 0 0 24px;
            width: 24px;
            height: 24px;

            @include breakpoint (mobile) {
                width: 18px;
                height: 18px;
                flex: 0 0 18px;
                background-size: contain;
            }
        }

        .mx-btn-icon-left,
        .mx-btn-icon-right {
            background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14.2292 12.4706L16 20L8 12.4706L16 4L14.2292 12.4706Z' fill='%23cccccc'/%3E%3C/svg%3E%0A");
            transition: $transition;

            &:hover {
                background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14.2292 12.4706L16 20L8 12.4706L16 4L14.2292 12.4706Z' fill='%23000000'/%3E%3C/svg%3E%0A");
            }
        }

        .mx-btn-icon-double-left,
        .mx-btn-icon-double-right {
            background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.2292 12.4706L12 20L4 12.4706L12 4L10.2292 12.4706Z' fill='%23cccccc'/%3E%3Cpath d='M18.2292 12.4706L20 20L12 12.4706L20 4L18.2292 12.4706Z' fill='%23cccccc'/%3E%3C/svg%3E%0A");
            transition: $transition;

            &:hover {
                background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.2292 12.4706L12 20L4 12.4706L12 4L10.2292 12.4706Z' fill='%23000000'/%3E%3Cpath d='M18.2292 12.4706L20 20L12 12.4706L20 4L18.2292 12.4706Z' fill='%23000000'/%3E%3C/svg%3E%0A");
            }
        }

        .mx-btn-icon-double-left {
            order: 0;
        }

        .mx-btn-icon-left {
            order: 1;
        }

        .mx-btn-icon-right {
            order: 3;
        }

        .mx-btn-icon-double-right {
            order: 4;
        }

        .mx-btn-icon-right,
        .mx-btn-icon-double-right {
            transform: rotate(180deg);
        }

        .mx-table {
            margin: 0;
        }

        .mx-table-year {
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            max-width: 244px;
            margin: -10px;

            tr {
                display: flex;
                justify-content: center;
                flex: 0 1 50%;
                padding: 10px;
            }

            td {
                flex: 0 1 100%;
                font-size: 16px;
                font-weight: 700;
                line-height: 1.4;
                color: $token-colors-black-20;
                cursor: pointer;
                transition: color $transition;

                &:hover,
                &:focus {
                    color: #EE7918;
                }

                &:first-child {
                    padding-right: 10px;
                }

                &:last-child {
                    padding-left: 10px;
                }
            }
        }

        .mx-table-month {
            width: 224px;
            height: 184px;

            td {
                font-size: 16px;
                font-weight: 700;
                line-height: 1.4;
                text-transform: capitalize;
                color: $token-colors-black-20;
                cursor: pointer;
                transition: color $transition;

                &:hover,
                &:focus {
                    color: #EE7918;
                }
            }
        }

        .mx-table-date {

            th,
            td {
                font-size: 13px;
                line-height: 18px;
            }

            th {
                padding-bottom: 10px;
                text-transform: uppercase;
                color: rgba($token-colors-black, .6);
            }

            .cell {
                cursor: pointer;

                &.disabled {
                    opacity: 0.2;
                    pointer-events: none;
                }

                > div {
                    width: 32px;
                    height: 32px;
                    padding: 7px 0;
                    margin: 2px 0;
                    text-align: center;

                    @include breakpoint (mobile) {
                        height: 24px;
                    }
                }

                &.not-current-month {
                    opacity: .2;
                }

                &.active {

                    div {
                        color: $token-colors-white;
                        background-color: #EE7918;
                        border-radius: 50%;

                        &:hover {
                            color: $token-colors-black
                        }
                    }

                }

                &.hover-in-range {

                    div {
                        background-color: #F1F1F1;
                    }
                }

                &:hover {

                    div {
                        box-shadow: inset 0 0 0 1px rgba($token-colors-black, .2);
                        background-color: #F1F1F1;
                        border-radius: 50%;
                    }
                }
            }
        }
    }

    &.is-disabled {
        opacity: .1;
        pointer-events: none;
    }

    @include breakpoint(v-tablet) {
        height: 44px;
    }

    @include breakpoint(mobile) {
        height: 40px;
    }
}
</style>
